const BYTE_FORMAT_DEFAULTS = {
  base: 2,
  locale: "en-US"
};
const BYTE_SIZES = [
  {
    short: "Bytes",
    long: "Bytes"
  },
  {
    short: "KB",
    long: "Kilobytes"
  },
  {
    short: "MB",
    long: "Megabytes"
  },
  {
    short: "GB",
    long: "Gigabytes"
  },
  {
    short: "TB",
    long: "Terabytes"
  },
  {
    short: "PB",
    long: "Petabytes"
  },
  {
    short: "EB",
    long: "Exabytes"
  },
  {
    short: "ZB",
    long: "Zettabytes"
  },
  {
    short: "YB",
    long: "Yottabytes"
  }
];
function bytes(value, options) {
  if (typeof value !== "string" || value.length === 0)
    throw new TypeError(
      `Value is not a string or is empty. value=${JSON.stringify(value)}`
    );
  if (value.length > 100)
    throw new TypeError(
      `Value exceeds the maximum length of 100 characters. value=${JSON.stringify(value)}`
    );
  const match = /^(?<value>-?(?:\d+(([.,])\d+)*)?[.,]?\d+) *(?<type>bytes?|b|kb|kib|mb|mib|gb|gib|tb|tib|pb|pib|eb|eib|zb|zib|yb|yib|(kilo|kibi|mega|mebi|giga|gibi|tera|tebi|peta|pebi|exa|exbi|zetta|zebi|yotta|yobi)?bytes)?$/i.exec(
    value
  );
  const groups = match?.groups;
  if (!groups)
    return Number.NaN;
  const n = parseLocalizedNumber(groups.value, options?.locale);
  const type = (groups.type || "Bytes").toUpperCase().replace(/^KIBI/, "KILO").replace(/^MIBI/, "MEGA").replace(/^GIBI/, "GIGA").replace(/^TEBI/, "TERA").replace(/^PEBI/, "PETA").replace(/^EXBI/, "EXA").replace(/^ZEBI/, "ZETTA").replace(/^YIBI/, "YOTTA").replace(/^(.)IB$/, "$1B");
  const level = BYTE_SIZES.findIndex(
    (unit) => unit.short[0].toUpperCase() === type[0]
  );
  const base = fromBase(options?.base || BYTE_FORMAT_DEFAULTS.base);
  return n * base ** level;
}
const parseBytes = bytes;
function formatBytes(bytes2, options) {
  if (typeof bytes2 !== "number" || !Number.isFinite(bytes2))
    throw new TypeError(
      `Bytesize is not a number. bytes=${Number.isNaN(bytes2) ? "NaN" : JSON.stringify(bytes2)}`
    );
  const {
    decimals = 0,
    long = false,
    unit: requestedUnit,
    locale = BYTE_FORMAT_DEFAULTS.locale,
    base: givenBase,
    ...l10nOptions
  } = options || {};
  const base = fromBase(givenBase || BYTE_FORMAT_DEFAULTS.base);
  const absoluteBytes = Math.abs(bytes2);
  const requestedUnitIndex = BYTE_SIZES.findIndex(
    (unit2) => unit2.short === requestedUnit
  );
  if (bytes2 === 0)
    return `0 ${BYTE_SIZES[Math.min(0, Math.max(requestedUnitIndex, BYTE_SIZES.length - 1))][long ? "long" : "short"]}`;
  const level = requestedUnitIndex >= 0 ? requestedUnitIndex : Math.min(
    Math.floor(Math.log(absoluteBytes) / Math.log(base)),
    BYTE_SIZES.length - 1
  );
  const unit = BYTE_SIZES[level][long ? "long" : "short"];
  const value = bytes2 / base ** level;
  const fractionDigits = decimals < 0 ? void 0 : decimals;
  const formattedValue = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    ...l10nOptions
  }).format(value);
  return `${formattedValue} ${unit}`;
}
function parseLocalizedNumber(stringNumber, locale = BYTE_FORMAT_DEFAULTS.locale) {
  const thousandSeparator = new Intl.NumberFormat(locale).format(11111).replace(/\p{Number}/gu, "");
  const decimalSeparator = new Intl.NumberFormat(locale).format(1.1).replace(/\p{Number}/gu, "");
  return Number.parseFloat(
    stringNumber.replace(new RegExp(`\\${thousandSeparator}`, "g"), "").replace(new RegExp(`\\${decimalSeparator}`), ".")
  );
}
const byteConversionUtils = BYTE_SIZES.reduce(
  (acc, { long }, level) => {
    if (long === "Bytes")
      return acc;
    const functionName = long.toLowerCase();
    acc[functionName] = (value, opts) => value * fromBase(opts?.base || BYTE_FORMAT_DEFAULTS.base) ** level;
    return acc;
  },
  {}
);
const {
  kilobytes,
  megabytes,
  gigabytes,
  terabytes,
  petabytes,
  exabytes,
  zettabytes,
  yottabytes
} = byteConversionUtils;
function createBytes(options) {
  const byteFormat = { ...BYTE_FORMAT_DEFAULTS, ...options };
  const byteConversionUtils2 = BYTE_SIZES.reduce(
    (acc, { long }, level) => {
      if (long === "Bytes")
        return acc;
      const functionName = long.toLowerCase();
      acc[functionName] = (value, opts) => {
        return value * fromBase(opts?.base || byteFormat.base) ** level;
      };
      return acc;
    },
    {}
  );
  const parse = {
    bytes: (value, opts) => bytes(value, {
      base: byteFormat.base,
      locale: byteFormat.locale,
      ...opts
    })
  }.bytes;
  return {
    ...byteConversionUtils2,
    bytes: parse,
    parseBytes: parse,
    formatBytes: (value, opts) => formatBytes(value, {
      base: byteFormat.base,
      locale: byteFormat.locale,
      ...opts
    })
  };
}
function fromBase(base) {
  if (base === 2)
    return 1024;
  if (base === 10)
    return 1e3;
  throw new TypeError(`Unsupported base. base=${base}`);
}

export { bytes, createBytes, exabytes, formatBytes, gigabytes, kilobytes, megabytes, parseBytes, petabytes, terabytes, yottabytes, zettabytes };
